/**
 * Defines the data contracts shared by the reusable Flow composition.
 * Dashboards choose loader row keys through FlowDashboardConfig; this module also contains the
 * breakdown-table contract and shared labels used by chart and table projections.
 */
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';

/** How a daily series is reduced over the visible range. */
export type FlowRangeAggregation = 'sum' | 'mean';

/** X axis of a Flow chart. */
export type FlowAxis = 'date' | 'install_day' | 'active_day';

/** Top-level breakdown order: chart ranking or visible-range value. */
export type FlowBreakdownSort = 'ranking' | 'desc' | 'asc';

export function getFlowAxis(context: IQueryContext): FlowAxis {
    const axis = context.settings[QueryContextSettingID.FLOW_X_AXIS];
    return axis === 'install_day' || axis === 'active_day' ? axis : 'date';
}

export function getFlowAccumulation(
    context: IQueryContext
): 'daily' | 'running' {
    return context.settings[QueryContextSettingID.FLOW_ACCUMULATION] ?? 'daily';
}

/** Generic long-format row; its date, value and grouping keys come from FlowDashboardConfig. */
export interface FlowDashboardRow {
    [key: string]: string | number | null | undefined;
}

/** One coherent chart-loader emission for the interactive Flow views. */
export interface FlowDashboardData {
    rows: FlowDashboardRow[];
    rangeAggregation: FlowRangeAggregation;
}

export function isFlowDashboardData(data: unknown): data is FlowDashboardData {
    return (
        typeof data === 'object' &&
        data !== null &&
        Array.isArray((data as FlowDashboardData).rows) &&
        ((data as FlowDashboardData).rangeAggregation === 'sum' ||
            (data as FlowDashboardData).rangeAggregation === 'mean')
    );
}

/** One breakdown-table row (one split group on one flow side). */
export interface FlowBreakdownRow {
    name: string;
    value: number;
    share: number;
    color: string;
    /** Prefixed Flow series name this row toggles on the chart. Empty = not a series. */
    seriesName: string;
    /** Mirror of the chart legend: true when this series is currently hidden. */
    hidden: boolean;
    /** Rows revealed on expansion, for example sources folded into Remaining. */
    children?: FlowBreakdownRow[];
    /** Stable identity for the table's expand state (defaults to seriesName). */
    key?: string;
}

/** Shared labels for synthetic Flow series. */
export const FLOW_REMAINING_LABEL = 'Remaining';
export const FLOW_IN_PREFIX = '▲';
export const FLOW_OUT_PREFIX = '▼';
/** Synthetic Remaining bucket → neutral grey, outside the categorical palette. */
export const FLOW_REMAINING_COLOR = '#9e9e9e';
